import { FC, useMemo } from "react";
import { CartesianGrid, Legend, Line, LineChart, XAxis, YAxis } from "recharts";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { SKUAnalytics, TagAnalytics } from "@/data/types";
import { formatByPeriod } from "@/lib/utils";

const chartConfig = {
  verifications: {
    label: "Page Views",
  },
  consents: {
    label: "Email Consent",
  },
  claims: {
    label: "Claim Count",
  },
  resets: {
    label: "Reset Count",
  },
} satisfies ChartConfig;

type Props = {
  data?: SKUAnalytics | TagAnalytics;
};

export const ViewsChart: FC<Props> = ({ data }) => {
  const chartData = useMemo(() => {
    if (!data) {
      return [];
    }

    return Object.keys(data.tag_view_verifications_data).map((time) => {
      return {
        time,
        verifications: data.tag_view_verifications_data[time],
        claims: data.claim_data[time],
        resets: data.reset_data[time],
        consents: data.email_consent_claim_data[time],
      };
    });
  }, [data]);

  return (
    <Card className="flex-1">
      <CardHeader>
        <CardTitle>Tag Interactions</CardTitle>
        <CardDescription>
          Page views, accepted marketing consent, and ownership claims or resets
          over time
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ChartContainer config={chartConfig} className="h-[240px] w-full">
          <LineChart accessibilityLayer data={chartData}>
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey="time"
              tickLine={false}
              axisLine={false}
              tickMargin={10}
              tickFormatter={(value) => formatByPeriod(value, data?.period)}
            />
            <YAxis axisLine={false} tickLine={false} />
            <ChartTooltip
              cursor={false}
              content={
                <ChartTooltipContent
                  labelFormatter={(value) =>
                    formatByPeriod(value, data?.period)
                  }
                />
              }
            />

            <Line
              name="Page Views"
              dataKey="verifications"
              type="monotone"
              stroke="var(--color-blue-600)"
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
            />
            <Line
              name="Marketing Consent"
              dataKey="consents"
              type="monotone"
              stroke="var(--color-teal-500)"
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
            />
            <Line
              name="Claims"
              dataKey="claims"
              type="monotone"
              stroke="var(--color-sky-500)"
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
            />
            <Line
              name="Resets"
              dataKey="resets"
              type="monotone"
              stroke="var(--color-fuchsia-500)"
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
            />
            <Legend iconSize={8} iconType="square" verticalAlign="bottom" />
          </LineChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
};
